import { Zap, Shield, Crown, Key, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';

interface Plan {
  id: string;
  name: string;
  token_quota: number;
  price_usd: number;
  max_cores: number;
}

interface BillingStatus {
  plan_id: string;
  plan_name: string;
  tokens_used: number;
  tokens_quota: number;
  api_token_hash: string;
  is_shared: boolean;
}

export function BillingPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [sharedTokenInput, setSharedTokenInput] = useState('');
  const [savingToken, setSavingToken] = useState(false);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        fetch('/v1/plans'),
        fetch('/v1/billing/status', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
        })
      ]);
      const plansData = await plansRes.json();
      const statusData = await statusRes.json();
      if (Array.isArray(plansData)) setPlans(plansData);
      setStatus(statusData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpgrade = async (plan: Plan) => {
    if (status?.is_shared) {
      return alert("Bạn đang dùng chung token của tài khoản khác, không thể tự nâng cấp. Hãy huỷ dùng chung trước (tính năng đang phát triển).");
    }
    setUpgrading(plan.id);
    try {
      const res = await fetch('/v1/billing/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('eric_token')}`
        },
        body: JSON.stringify({
          new_plan_id: plan.id,
          new_token_quota: plan.token_quota,
          new_max_cores: plan.max_cores
        })
      });
      const data = await res.json();
      if (data.success) {
        setTimeout(() => {
          fetchData();
          setUpgrading(null);
        }, 1000);
      } else {
        alert("Upgrade failed: " + data.error);
        setUpgrading(null);
      }
    } catch (err) {
      alert("Network error!");
      setUpgrading(null);
    }
  };

  const handleSaveSharedToken = async () => {
    if (!sharedTokenInput.trim()) return alert("Vui lòng nhập token!");
    setSavingToken(true);
    try {
      const res = await fetch('/v1/billing/shared-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('eric_token')}`
        },
        body: JSON.stringify({ shared_token: sharedTokenInput.trim() })
      });
      const data = await res.json();
      if (data.success) {
        alert("Đã kết nối dùng chung token thành công!");
        setSharedTokenInput('');
        fetchData();
      } else {
        alert("Lỗi: " + data.error);
      }
    } catch (err) {
      alert("Network error!");
    } finally {
      setSavingToken(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!confirm("Tạo mã mới sẽ làm mã cũ vô hiệu. Những người đang dùng chung mã của bạn sẽ không bị ảnh hưởng (họ đã liên kết theo tài khoản). Bạn có chắc chắn muốn tạo mã mới?")) return;
    try {
      const res = await fetch('/v1/billing/generate-token', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
      } else {
        alert("Lỗi tạo mã: " + data.error);
      }
    } catch (err) {
      alert("Network error!");
    }
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading billing info...</div>;

  const availableCredits = Math.max(0, (status?.tokens_quota || 0) - (status?.tokens_used || 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', maxWidth: '1000px', margin: '0 auto' }}>
      
      <div style={{ marginBottom: '8px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px', color: 'var(--text-primary)' }}>Billing & Usage</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Manage your API credits, top up your balance, and upgrade your subscription plan.</p>
      </div>

      {/* Top Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>
            AVAILABLE CREDITS
            <span>💰</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--accent-primary)' }}>
            {availableCredits.toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {status?.is_shared ? "Shared from another account" : "Shared across all your generated API keys"}
          </div>
        </div>

        <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>
            CURRENT PLAN
            <span>✨</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)' }}>
            {status?.plan_name || 'No Plan'}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Priority access & higher rate limits
          </div>
        </div>
      </div>

      {/* Shared Token Section */}
      <div className="glass-panel" style={{ padding: '24px', border: '1px solid rgba(139, 92, 246, 0.3)', background: 'linear-gradient(rgba(139, 92, 246, 0.02), rgba(0,0,0,0.2))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: 'var(--text-primary)', fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px' }}>
          <Key size={16} color="var(--accent-amber)" />
          YOUR SHARED TOKEN KEY
        </div>
        
        <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
          Nhập Shared Token Key để dùng chung tài khoản với user trả phí (Paid User). Bạn sẽ được chia sẻ quota tokens và quyền lợi model từ tài khoản đó.
        </p>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          <input 
            type="text" 
            placeholder="Nhập Shared Token Key..." 
            value={sharedTokenInput}
            onChange={e => setSharedTokenInput(e.target.value)}
            disabled={status?.is_shared || savingToken}
            style={{ flex: 1, padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }}
          />
          <button 
            className="btn btn-primary" 
            onClick={handleSaveSharedToken} 
            disabled={status?.is_shared || savingToken}
            style={{ padding: '0 24px', whiteSpace: 'nowrap' }}
          >
            {savingToken ? 'Đang lưu...' : 'Lưu Token'}
          </button>
        </div>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
              Mã chia sẻ của bạn
            </div>
            <div style={{ background: 'rgba(139, 92, 246, 0.1)', padding: '6px 12px', borderRadius: '6px', color: 'var(--accent-primary)', fontFamily: 'monospace', fontSize: '14px', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
              {status ? (status.api_token_hash || 'Chưa có mã (Hãy bấm Tạo mã mới)') : 'Đang tải...'}
            </div>
          </div>
          <button onClick={handleRegenerateToken} className="btn btn-glass" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '13px' }}>
            <RefreshCw size={14} />
            Tạo mã mới
          </button>
        </div>
      </div>

      <div style={{ marginTop: '16px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px', color: 'var(--text-primary)' }}>Upgrade your plan</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          
          {plans.map((p) => {
            const isCurrent = status?.plan_id === p.id && !status?.is_shared;
            const isPopular = p.price_usd > 0 && p.price_usd < 50;
            
            let Icon = Zap;
            let color = 'var(--text-secondary)';
            if (p.price_usd > 0) { Icon = Shield; color = 'var(--accent-primary)'; }
            if (p.price_usd >= 90) { Icon = Crown; color = 'var(--accent-amber)'; }

            return (
              <div key={p.id} className={`glass-panel ${isPopular ? 'glow-box' : ''}`} style={{ padding: '32px', display: 'flex', flexDirection: 'column', borderColor: isPopular ? 'var(--accent-primary)' : 'var(--border)', transform: isPopular ? 'scale(1.02)' : 'none' }}>
                <div style={{ color, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Icon size={28} />
                  {isPopular && <span style={{ background: 'var(--gradient-main)', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, color: '#fff' }}>POPULAR</span>}
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: 600 }}>{p.name}</h3>
                <div style={{ fontSize: '36px', fontWeight: 700, margin: '16px 0' }}>\${Number(p.price_usd)} <span style={{ fontSize: '16px', color: 'var(--text-muted)', fontWeight: 400 }}>/ month</span></div>
                
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--text-primary)' }}>
                  <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> {(p.token_quota / 1000000).toFixed(1)}M Tokens Virtual Context</li>
                  <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> {p.max_cores} Concurrent Agent Sessions</li>
                  <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> Permanent Memory TTL</li>
                  {p.price_usd > 0 && <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> Server-side Auto-Indexing (GitHub)</li>}
                </ul>
                
                <button 
                  onClick={() => handleUpgrade(p)}
                  disabled={isCurrent || upgrading === p.id || status?.is_shared}
                  className={isPopular && !isCurrent ? "btn btn-primary" : "btn btn-glass"} 
                  style={{ marginTop: 'auto', width: '100%', opacity: isCurrent ? 0.5 : 1, cursor: isCurrent || status?.is_shared ? 'not-allowed' : 'pointer' }}
                >
                  {upgrading === p.id ? 'Processing...' : isCurrent ? 'Current Plan' : `Upgrade to ${p.name}`}
                </button>
              </div>
            );
          })}

        </div>
      </div>

    </div>
  );
}
